import { useAuth0 } from '@auth0/auth0-react';
import * as Sentry from '@sentry/browser';
import { useCallback } from 'react';
import type z from 'zod/v4';
import { type FetchConfig, fetchApi } from '~/api/fetch';
import { getAuthErrorCode } from '~/errors/auth-error';
import { InvalidRequestSchemaError } from '~/errors/invalid-request-schema';
import { InvalidResponseSchemaError } from '~/errors/invalid-response-schema';

export function useFetch() {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const fetchData = useCallback(
    async <RequestSchema extends z.ZodType, ResponseSchema extends z.ZodType>(
      url: string,
      schemas: { request?: RequestSchema; response: ResponseSchema },
      config?: FetchConfig<z.infer<RequestSchema>>,
    ) => {
      if (config?.body && !schemas.request) {
        throw new Error('Schema missing for request body!');
      }

      if (schemas.request) {
        const validatedRequest = schemas.request.safeParse(config?.body);

        if (!validatedRequest.success) {
          const { error } = validatedRequest;
          const exception = new InvalidRequestSchemaError(error);

          Sentry.captureException(exception, { extra: { requestURL: url } });
          throw exception;
        }
      }

      const isSkipAuth = typeof SKIP_AUTH !== 'undefined' && SKIP_AUTH === 'true' && PROD !== 'true';

      const authHeaders: Record<string, string> = {};
      if (isSkipAuth) {
        if (typeof DEV_ORG_ID !== 'undefined' && DEV_ORG_ID) {
          authHeaders['X-granular-energy-organization-id'] = DEV_ORG_ID;
        }
      } else {
        let token: string;
        try {
          token = await getAccessTokenSilently();
        } catch (e) {
          Sentry.captureException(e, {
            extra: { authErrorCode: getAuthErrorCode(e), requestURL: url },
            tags: { authTokenRefresh: 'failed' },
          });
          await loginWithRedirect();
          throw e;
        }
        authHeaders.Authorization = `Bearer ${token}`;
      }

      const response = await fetchApi<z.infer<RequestSchema>, z.infer<ResponseSchema>>(url, {
        ...config,
        options: {
          ...config?.options,
          headers: {
            ...authHeaders,
            ...config?.options?.headers,
          },
        },
      });

      const validatedResponse = schemas.response.safeParse(response);

      if (!validatedResponse.success) {
        // Keep this logged, as schema mismatches usually fail silently and are hard to debug
        console.error('Schema mismatch. Validated response: ', validatedResponse);

        const { error } = validatedResponse;
        const exception = new InvalidResponseSchemaError(error);

        Sentry.captureException(exception, { extra: { responseURL: url } });
        throw exception;
      }

      return validatedResponse.data;
    },
    [getAccessTokenSilently, loginWithRedirect],
  );

  return { fetchData };
}
